import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  alpha,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";

interface CameraModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageDataUrl: string) => void;
}

const CameraModal: React.FC<CameraModalProps> = ({ open, onClose, onCapture }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [captured, setCaptured] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const startCamera = useCallback(async () => {
    setCaptured(null);
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err: any) {
      if (err.name === "NotAllowedError") {
        setCameraError("Camera permission denied. Please allow camera access in your browser settings.");
      } else if (err.name === "NotFoundError") {
        setCameraError("No camera found on this device.");
      } else {
        setCameraError("Could not access camera. Please try again.");
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (open) startCamera();
    return () => stopCamera();
  }, [open, startCamera, stopCamera]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    setCaptured(canvas.toDataURL("image/jpeg", 0.9));
    stopCamera();
  };

  const handleRetake = () => {
    setCaptured(null);
    startCamera();
  };

  const handleConfirm = () => {
    if (captured) {
      onCapture(captured);
      handleClose();
    }
  };

  const handleClose = () => {
    stopCamera();
    setCaptured(null);
    setCameraError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CameraAltIcon sx={{ fontSize: 20, color: "primary.main" }} />
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Capture Photo</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Camera / Preview */}
      <Box sx={{ position: "relative", width: "100%", pt: "100%", bgcolor: "#000" }}>
        {cameraError ? (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: 4 }}>
            <VideocamOffIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.3)", mb: 1.5 }} />
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", textAlign: "center" }}>
              {cameraError}
            </Typography>
          </Box>
        ) : captured ? (
          <img
            src={captured}
            alt="Captured"
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
          />
        )}

        {/* Circular overlay guide */}
        {!captured && !cameraError && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.25)",
              m: "12%",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, p: 2.5 }}>
        {cameraError ? (
          <Button variant="outlined" onClick={handleClose} sx={{ borderRadius: "10px", textTransform: "none" }}>
            Close
          </Button>
        ) : captured ? (
          <>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={handleRetake}
              sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13 }}
            >
              Retake
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={handleConfirm}
              sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, boxShadow: "none" }}
            >
              Use Photo
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<CameraAltIcon />}
              onClick={handleCapture}
              sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, boxShadow: "none" }}
            >
              Capture
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default CameraModal;
